import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { BookMarked, Globe2, Layers } from 'lucide-react';
import { API_BASE } from '../../config/api';

const API = `${API_BASE}/api`;
export const DEFAULT_NAVI_OPTIONS = { jurisdiction: 'ZA', depth: 'standard', source_ids: [] };
const JURISDICTIONS = [['ZA', 'South Africa (default)'], ['GLOBAL', 'International'], ['UK', 'United Kingdom'], ['US', 'United States'], ['EU', 'European Union']];
const DEPTHS = [['overview', 'Overview', 'Essentials only'], ['standard', 'Standard', 'Explanations + examples'],
  ['in_depth', 'In-depth', 'Worked example, SA case study, common mistakes, self-check']];

const selectCls = 'w-full h-9 mt-1 rounded-md bg-zinc-800 border border-zinc-700 text-white text-sm px-2';

// NAVI generation controls: jurisdiction, depth and trusted sources from the Knowledge Library.
export default function NaviGenerationOptions({ value, onChange, token }) {
  const [docs, setDocs] = useState(null);
  useEffect(() => {
    axios.get(`${API}/admin/knowledge`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setDocs(r.data.documents || [])).catch(() => setDocs([]));
  }, [token]);
  const set = (k, v) => onChange({ ...value, [k]: v });
  const toggle = (id) => set('source_ids', value.source_ids.includes(id) ? value.source_ids.filter((x) => x !== id) : [...value.source_ids, id]);

  return (
    <div className="space-y-4 rounded-xl border border-zinc-800 bg-zinc-900/50 p-4" data-testid="navi-generation-options">
      <div className="grid sm:grid-cols-2 gap-4">
        <label className="text-sm text-zinc-400"><span className="flex items-center gap-1.5"><Globe2 className="h-3.5 w-3.5" />Jurisdiction</span>
          <select value={value.jurisdiction} onChange={(e) => set('jurisdiction', e.target.value)} className={selectCls} data-testid="navi-jurisdiction-select">
            {JURISDICTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </label>
        <label className="text-sm text-zinc-400"><span className="flex items-center gap-1.5"><Layers className="h-3.5 w-3.5" />Depth</span>
          <select value={value.depth} onChange={(e) => set('depth', e.target.value)} className={selectCls} data-testid="navi-depth-select">
            {DEPTHS.map(([v, l, d]) => <option key={v} value={v}>{l}: {d}</option>)}
          </select>
        </label>
      </div>
      <div>
        <div className="flex items-center justify-between">
          <p className="text-sm text-zinc-400 flex items-center gap-1.5"><BookMarked className="h-3.5 w-3.5" />Trusted sources <span className="text-zinc-600">(optional)</span></p>
          <Link to="/admin/knowledge" target="_blank" className="text-xs text-teal-400 hover:text-teal-300" data-testid="navi-manage-library-link">Manage library</Link>
        </div>
        {docs === null ? <p className="text-xs text-zinc-500 mt-2">Loading library…</p>
          : docs.length === 0 ? <p className="text-xs text-zinc-500 mt-2">No sources yet. Upload Acts, guidance notes or policies in the Knowledge Library so NAVI writes only from them.</p>
            : (
              <div className="mt-2 max-h-40 overflow-y-auto space-y-1.5 pr-1">
                {docs.map((d) => (
                  <label key={d.doc_id} className="flex items-center gap-2 text-sm text-zinc-200 cursor-pointer">
                    <input type="checkbox" checked={value.source_ids.includes(d.doc_id)} onChange={() => toggle(d.doc_id)} className="accent-teal-500" data-testid={`navi-source-${d.doc_id}`} />
                    <span className="truncate">{d.title}</span><span className="text-[10px] text-zinc-500 shrink-0">{d.category}</span>
                  </label>
                ))}
              </div>
            )}
        {value.source_ids.length > 0 && <p className="text-[11px] text-teal-300 mt-2">NAVI will write only from the {value.source_ids.length} selected source{value.source_ids.length === 1 ? '' : 's'} and cite them per section.</p>}
      </div>
    </div>
  );
}
